// src/index.js - Updated with environment variable API URLs and improved error handling
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { store } from './store/store';

// Import components from their respective files
import MarsLoginForm from './component/Login';
import MarsSignupForm from './component/SignUp';
import Dashboard from './component/Dashboard';
import AffiliateLayout from './component/AffiliateRecruitments';
import AffiliateLayoutDirect from './component/AffiliateDirectory';
import SerpLayout from './component/SerpCheckers';
import KeywordLayout from './component/KeywordLayout';
import CompetitorSpyLayout from './component/competitorSpy/CompetitorSpyLayout';
import AffiliateGapLayout from './component/AffiliateGapLayout';
import ContactFinderLayout from './component/ContactFinderLayout';
import SocialMediaFinderLayout from './component/SocialMediaFinderLayout';
import ManualAffiliateAddLayout from './component/ManualAffiliateAddLayout';
import AffiliateAgentLayout from './component/AffiliateAgentLayout';
import PricingPage from './component/PricingPage';
import PricingPlanLayout from './component/PricingPlanLayout';
import ProfilePageLayout from './component/ProfilePageLayout';
import LiveAffiliateLayout from './component/liveAffiliate/liveAffiliateLayout';
import DocumentationLayout from './component/documentation/DocumentationLayout';
import ForgetPassword from './component/forgetpassword/forgetpassword';
import UntappedAffiliate from './component/untapped/untappedLayout';
import SmartTableLayout from './component/untapped/smartTableLayout';
import SentBox from './component/sentBox/sentBoxLayout';
import EmailSettingsLayout from './component/emailSetting/emailSettingLayout';
import AiApprovalLayout from './component/aiApproval/aiApprovalLayout';
import AffiliateRecruitmentToday from './component/affiliateRecruitmentoday';
import UpdateBrand from './component/updateBrand/updateBrandLayout';
import UserPanel from './component/userPanel/userPanelLayout';
import NotificationAffiliate from './component/notificationAffiliate/NotificatinAffiliateLayout';
// Import Payment Success Component
import PaymentSuccess from './component/PaymentSuccess';
import PrivacyPolicy from './component/terms/PrivacyPolicy';
import TermsOfService from './component/terms/TermsOfService';
import PositionTracker from './component/positionTracker/PositionTrackerLayout';
import ManualAddBrand from './component/manualAdd/ManualAddBrand';
import BrandAdd from './component/brandAdd/BrandAdd';
import ManualDomainAddLayout from './component/manualDomainAdd/ManualDomainAddLayout';
import AffiliateFinderLayout from './component/affiliateFinder/AffiliateFinderLayout';
import AffiliateFinderNoCostLayout from './component/affiliateFinderNoCost/AffiliateFinderNoCostLayout';
import TermsCheckerLayout from './component/termsChecker/TermsCheckerLayout';
import TermsCheckerV3Layout from './component/termsChecker/TermsCheckerV3Layout';
// Import standard Redux hooks
import { useSelector, useDispatch } from 'react-redux';
// Import auth actions from the correct path
import { loginSuccess, logout } from './store/slices/authSlice';
import SandBoxLayout from './component/sandBox/sandBoxLayout';
import CrmPipelineLayout from './component/crm/CrmPipelineLayout';
import KeywordGapLayout from './component/KeywordGapLayout';
import DebugConsoleLayout from './component/debugConsole/DebugConsoleLayout';
import { ThemeProvider } from './context/ThemeContext';

// ==================== API CONFIGURATION ====================
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
};

// ==================== GENERAL SESSION PROTECTED ROUTE (USING ENV VARIABLE) ====================
const SessionProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const verifySessionWithBackend = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl();

        // Check if there's a temp_token in URL (from email verification)
        const urlParams = new URLSearchParams(location.search);
        const tempToken = urlParams.get('temp_token');
        
        if (tempToken) {
          
          const tokenExchangeResponse = await fetch(`${apiUrl}/api/auth/exchange-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              tempToken: tempToken
            })
          });

          if (tokenExchangeResponse.ok) {
            const tokenData = await tokenExchangeResponse.json();
            
            // Remove temp_token from URL
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('temp_token');
            window.history.replaceState({}, '', newUrl);
            
          } else {
            console.error('❌ Token exchange failed:', tokenExchangeResponse.status);
            setError('Unable to verify your access. Please try logging in again.');
            setIsLoading(false);
            return;
          }
        }

        const response = await fetch(`${apiUrl}/api/auth/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            timestamp: Date.now(),
            requestType: 'session_verification'
          })
        });


        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.hasSession) {
            setHasValidSession(true);
          } else {
            setHasValidSession(false);
            setError('Login timeout. Please sign in again to continue.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ HTTP Error:', {
            status: response.status,
            statusText: response.statusText,
            errorData
          });
          setHasValidSession(false);
          
          if (response.status === 401) {
            setError('Login timeout. Please sign in again to continue.');
          } else if (response.status === 403) {
            setError('Access denied. Please check your account permissions.');
          } else if (response.status >= 500) {
            setError('Server is temporarily unavailable. Please try again in a few moments.');
          } else {
            setError('Unable to verify your session. Please try again.');
          }
        }
      } catch (error) {
        console.error('Session verification error:', error);
        setHasValidSession(false);
        
        // More user-friendly error messages
        let userMessage;
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('NetworkError')) {
          userMessage = 'Network connection issue. Please try refreshing the page.';
        } else {
          userMessage = 'Something went wrong. Please try again or contact support if the issue persists.';
        }
        
        setError(userMessage);
      } finally {
        setIsLoading(false);
      }
    };

    verifySessionWithBackend();
  }, [location.search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying session...</p>
          <p className="mt-2 text-xs text-gray-400">Connecting to global affiliate network</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 mb-4 text-4xl">⏰</div>
            <h3 className="text-lg font-medium text-red-800 mb-3">Authentication Required</h3>
            <p className="text-red-700 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Sign In Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
              >
                Try Again
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              If this problem continues, please contact our support team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (hasValidSession) {
    return children;
  }

  return <Navigate to="/login?error=session_required" replace />;
};

// ==================== STANDARD PROTECTED ROUTE COMPONENT ====================
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  
  if (isAuthenticated && user?.emailVerified) {
    return children;
  }
  
  return <Navigate to="/login" replace />;
};

// ==================== PUBLIC ROUTE COMPONENT ====================
const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  
  if (isAuthenticated && user?.emailVerified) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// ==================== SESSION SYNC HOOK (USING ENV VARIABLE) ====================
const useSessionSync = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const syncSession = async () => {
    try {
      const apiUrl = getApiUrl();
      
      const response = await fetch(`${apiUrl}/api/auth/verify-session`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.hasSession && !isAuthenticated) {
          const userResponse = await fetch(`${apiUrl}/api/auth/me`, {
            method: 'GET',
            credentials: 'include'
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            if (userData.success) {
              dispatch(loginSuccess({
                user: userData.data
              }));
              return true;
            }
          }
        } else if (!data.hasSession && isAuthenticated) {
          dispatch(logout());
          return false;
        }
        
        return data.hasSession;
      }
    } catch (error) {
      console.error('Session sync error:', error);
      return false;
    }
  };

  return { syncSession };
};

// ==================== GLOBAL SESSION SYNC COMPONENT ====================
const GlobalSessionSync = () => {
  const { syncSession } = useSessionSync();
  
  useEffect(() => {
    syncSession();
    const interval = setInterval(syncSession, 5 * 60 * 1000);
    const handleFocus = () => syncSession();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [syncSession]);
  
  return null;
};

// ==================== SUCCESS MESSAGE COMPONENT ====================
const SuccessMessage = () => {
  const location = useLocation();
  const [showMessage, setShowMessage] = useState(false);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isVerified = urlParams.get('verified') === 'true';
    
    if (isVerified) {
      setShowMessage(true);
      const timer = setTimeout(() => setShowMessage(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [location]);
  
  if (!showMessage) return null;
  
  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 text-green-400">✅</div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Email Verified Successfully!
            </h3>
            <p className="mt-1 text-sm text-green-700">
              Welcome to MARS! You can now access your dashboard.
            </p>
          </div>
          <div className="ml-auto pl-3">
            <button
              onClick={() => setShowMessage(false)}
              className="text-green-400 hover:text-green-600"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PRICING PAGE WRAPPER ====================
const PricingPageWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const hasToken = urlParams.get('token');
  const hasEmail = urlParams.get('email');
  
  // If token and email are present, allow access without session
  if (hasToken && hasEmail) {
    return <PricingPage />;
  }
  
  // Otherwise, require session
  return (
    <SessionProtectedRoute>
      <PricingPage />
    </SessionProtectedRoute>
  );
};

// ==================== MAIN APP COMPONENT ====================
const App = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  return (
    <>
      <GlobalSessionSync />
      <SuccessMessage />
      <Routes>
        {/* Root route */}
        <Route 
          path="/" 
          element={
            (isAuthenticated && user?.emailVerified) ? 
              <Navigate to="/dashboard" replace /> : 
              <Navigate to="/login" replace />
          } 
        />
        
        {/* Login Route */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <MarsLoginForm />
            </PublicRoute>
          }
        />

        {/* Signup Route */}
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <MarsSignupForm />
            </PublicRoute>
          }
        />
        
        {/* Forgot password Route */}
        <Route 
          path="/reset-password" 
          element={
            <PublicRoute>
              <ForgetPassword />
            </PublicRoute>
          } 
        />

     <Route 
  path="/privacy-policy" 
  element={<PrivacyPolicy />}
/>

<Route 
  path="/terms-of-service" 
  element={<TermsOfService />}
/>
        
        
        {/* Pricing Page - Public with token, protected without */}
        <Route 
          path="/pricing" 
          element={<PricingPageWrapper />}
        />

        {/* Payment Success Route */}
        <Route 
          path="/payment-success" 
          element={
            <SessionProtectedRoute>
              <PaymentSuccess />
            </SessionProtectedRoute>
          }
        />
        <Route
          path="/position-tracker"
          element={
            <SessionProtectedRoute>
              <PositionTracker />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manualAdd"
          element={
            <SessionProtectedRoute>
              <ManualAddBrand />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manual-domain-add"
          element={
            <SessionProtectedRoute>
              <ManualDomainAddLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/brand-add"
          element={
            <SessionProtectedRoute>
              <BrandAdd />
            </SessionProtectedRoute>
          }
        />

        {/* Protected Dashboard Route */}
        <Route 
          path="/dashboard" 
          element={
            <SessionProtectedRoute>
              <Dashboard />
            </SessionProtectedRoute>
          } 
        />
        
        {/* Feature Routes - Use SessionProtectedRoute for backend session verification */}
        <Route
          path="/affiliate-recruitment"
          element={
            <SessionProtectedRoute>
              <AffiliateLayout />
            </SessionProtectedRoute>
          }
        />

<Route 
          path="/SerpChecker" 
          element={
            <SessionProtectedRoute>
              <SerpLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route
          path="/affiliate-directory"
          element={
            <SessionProtectedRoute>
              <AffiliateLayoutDirect />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-finder"
          element={
            <SessionProtectedRoute>
              <AffiliateFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-finder-nocost"
          element={
            <SessionProtectedRoute>
              <AffiliateFinderNoCostLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/terms-checker"
          element={
            <SessionProtectedRoute>
              <TermsCheckerLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/terms-checker-v3"
          element={
            <SessionProtectedRoute>
              <TermsCheckerV3Layout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/keyword-explorer" 
          element={
            <SessionProtectedRoute>
              <KeywordLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/competitor-spy" 
          element={
            <SessionProtectedRoute>
              <CompetitorSpyLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/live-affiliate" 
          element={
            <SessionProtectedRoute>
              <LiveAffiliateLayout />
            </SessionProtectedRoute>
          } 
        />

         <Route 
          path="/notification" 
          element={
            <SessionProtectedRoute>
              <NotificationAffiliate />
            </SessionProtectedRoute>
          } 
        />

         <Route
          path="/untapped-opportunities"
          element={
            <SessionProtectedRoute>
              <SmartTableLayout />
            </SessionProtectedRoute>
          }
        />

        {/* Smart Table is now the main untapped opportunities table */}
        {/* <Route
          path="/smart-table"
          element={
            <SessionProtectedRoute>
              <SmartTableLayout />
            </SessionProtectedRoute>
          }
        /> */}

          <Route
          path="/sent-box" 
          element={
            <SessionProtectedRoute>
              <SentBox />
            </SessionProtectedRoute>
          } 
        />
          <Route 
          path="/sand-box" 
          element={
            <SessionProtectedRoute>
              <SandBoxLayout />
            </SessionProtectedRoute>
          } 
        />
        
        <Route
          path="/email-settings"
          element={
            <SessionProtectedRoute>
              <EmailSettingsLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/ai-approval"
          element={
            <SessionProtectedRoute>
              <AiApprovalLayout />
            </SessionProtectedRoute>
          }
        />

        <Route 
          path="/profile" 
          element={
            <SessionProtectedRoute>
              <ProfilePageLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/documentation" 
          element={
            <SessionProtectedRoute>
              <DocumentationLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/pricing-plan" 
          element={
            <SessionProtectedRoute>
              <PricingPlanLayout />
            </SessionProtectedRoute>
          } 
        />


         <Route 
          path="/update-brand" 
          element={
            <SessionProtectedRoute>
              <UpdateBrand />
            </SessionProtectedRoute>
          } 
        />

         <Route
          path="/user-panel"
          element={
            <SessionProtectedRoute>
              <UserPanel />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/debug-console"
          element={
            <SessionProtectedRoute>
              <DebugConsoleLayout />
            </SessionProtectedRoute>
          }
        />

        

         <Route 
          path="/affiliate-recruitment-today" 
          element={
            <SessionProtectedRoute>
              <AffiliateRecruitmentToday />
            </SessionProtectedRoute>
          } 
        />

        <Route
          path="/affiliate-gap"
          element={
            <SessionProtectedRoute>
              <AffiliateGapLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/keyword-gap"
          element={
            <SessionProtectedRoute>
              <KeywordGapLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/contact-finder"
          element={
            <SessionProtectedRoute>
              <ContactFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/social-media-finder"
          element={
            <SessionProtectedRoute>
              <SocialMediaFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manual-affiliate-add"
          element={
            <SessionProtectedRoute>
              <ManualAffiliateAddLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-agent"
          element={
            <SessionProtectedRoute>
              <AffiliateAgentLayout />
            </SessionProtectedRoute>
          }
        />
        
        <Route
          path="/crm-pipeline"
          element={
            <SessionProtectedRoute>
              <CrmPipelineLayout />
            </SessionProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// ==================== ROOT RENDER ====================
const root = ReactDOM.createRoot(document.getElementById('root'));
// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '271320243979-dt51m782dkpee5fsd967vfinbp8bicn5.apps.googleusercontent.com';

root.render(
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Provider store={store}>
        <ThemeProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </Provider>
    </GoogleOAuthProvider>
);

reportWebVitals();

// ==================== EXPORTS ====================
export { 
  SessionProtectedRoute, 
  ProtectedRoute, 
  PublicRoute, 
  useSessionSync 
};